"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { HelpCircle, CloudSun, Calculator, Camera, TrendingUp, Home, History, Sun, Settings } from "lucide-react";

type DataKey = "current" | "morning" | "noon";

type Dataset = {
  temp: number[];
  uv: number[];
  uvIndex: number;
  uvStatus: string;
  uvPct: number;
  goIcon: string;
  goText: string;
  sbText: string;
};

const hours = ["4am", "6am", "8am", "10am", "12pm", "2pm"];

const datasets: Record<DataKey, Dataset> = {
  current: {
    temp: [60, 65, 68, 66, 59, 50],
    uv: [0, 1, 5.7, 4.5, 2, 0],
    uvIndex: 2.9,
    uvStatus: "Moderate",
    uvPct: 30,
    goIcon: "🌙",
    goText: "It's nighttime — no UV at all!",
    sbText: "UV is moderate — apply SPF 30+ if going out",
  },
  morning: {
    temp: [56, 60, 65, 67, 68, 66],
    uv: [0, 0.5, 2, 4, 5.2, 5.7],
    uvIndex: 5.7,
    uvStatus: "High",
    uvPct: 65,
    goIcon: "⛅",
    goText: "Good morning! UV building — apply SPF now.",
    sbText: "UV is rising — great time to get some sun with protection",
  },
  noon: {
    temp: [66, 68, 69, 70, 68, 62],
    uv: [3, 4.5, 5.5, 6, 5, 2],
    uvIndex: 6.0,
    uvStatus: "Very High",
    uvPct: 80,
    goIcon: "🌤️",
    goText: "Peak UV now. Seek shade or apply SPF 50+.",
    sbText: "UV is very high — limit exposure between 10am–2pm",
  },
};

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

type Point = { x: number; y: number };

function getPoints(
  data: number[],
  w: number,
  h: number,
  padL: number,
  padR: number,
  padT: number,
  padB: number,
  minV: number,
  maxV: number
): Point[] {
  const pts: Point[] = [];
  const n = data.length;
  for (let i = 0; i < n; i++) {
    const x = padL + (i / (n - 1)) * (w - padL - padR);
    const y = padT + (1 - (data[i] - minV) / (maxV - minV)) * (h - padT - padB);
    pts.push({ x, y });
  }
  return pts;
}

function drawGridLines(ctx: CanvasRenderingContext2D, w: number, h: number, padL: number, padR: number, padT: number, padB: number) {
  const ySteps = 4;
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 0.7;
  for (let i = 0; i <= ySteps; i++) {
    const y = padT + (i / ySteps) * (h - padT - padB);
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(w - padR, y);
    ctx.stroke();
  }
}

function drawSmoothLine(ctx: CanvasRenderingContext2D, points: Point[], color: string, alpha: number) {
  if (!points.length) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 0; i < points.length - 1; i++) {
    const prev = points[i - 1] || points[i];
    const curr = points[i];
    const next = points[i + 1];
    const after = points[i + 2] || next;
    const t = 0.3;
    const cpx1 = curr.x + (next.x - prev.x) * t;
    const cpy1 = curr.y + (next.y - prev.y) * t;
    const cpx2 = next.x - (after.x - curr.x) * t;
    const cpy2 = next.y - (after.y - curr.y) * t;
    ctx.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, next.x, next.y);
  }

  ctx.strokeStyle = color;
  ctx.globalAlpha = alpha;
  ctx.lineWidth = 2.2;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function drawDots(ctx: CanvasRenderingContext2D, points: Point[], color: string) {
  for (const pt of points) {
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }
}

export function SunscreenedAppUi() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [key, setKey] = useState<DataKey>("current");
  const [animFrom, setAnimFrom] = useState<DataKey | null>(null);
  const [animStart, setAnimStart] = useState<number | null>(null);

  const d = datasets[key];

  const size = useMemo(() => ({ padL: 28, padR: 16, padT: 10, padB: 22 }), []);

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;

      const r = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setAnimFrom((prev) => prev ?? key);
      setAnimStart(null);
      setKey((k) => (k === "current" ? "morning" : k === "morning" ? "noon" : "current"));
    }, 5200);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const draw = (t: number) => {
      const r = wrap.getBoundingClientRect();
      const w = r.width;
      const h = r.height;
      ctx.clearRect(0, 0, w, h);

      const { padL, padR, padT, padB } = size;
      drawGridLines(ctx, w, h, padL, padR, padT, padB);

      let tempData = datasets[key].temp;
      let uvData = datasets[key].uv;

      if (animFrom) {
        const from = datasets[animFrom];
        const to = datasets[key];
        const tt = easeOutCubic(t);
        tempData = from.temp.map((v, i) => lerp(v, to.temp[i]!, tt));
        uvData = from.uv.map((v, i) => lerp(v, to.uv[i]!, tt));
      }

      const tempPts = getPoints(tempData, w, h, padL, padR, padT, padB, 50, 70);
      const uvPts = getPoints(uvData, w, h, padL, padR, padT, padB, 0, 6);

      // Line-only: no filled areas
      drawSmoothLine(ctx, uvPts, "#F97316", 0.95);
      drawSmoothLine(ctx, tempPts, "#3a9af5", 0.9);
      drawDots(ctx, uvPts, "#F97316");
      drawDots(ctx, tempPts, "#3a9af5");
    };

    const ANIM_DUR = 1400;

    const step = (ts: number) => {
      if (animFrom) {
        const start = animStart ?? ts;
        if (animStart == null) setAnimStart(start);
        const t = clamp((ts - start) / ANIM_DUR, 0, 1);
        draw(t);
        if (t < 1) requestAnimationFrame(step);
        else setAnimFrom(null);
      } else {
        draw(1);
        requestAnimationFrame(step);
      }
    };

    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [animFrom, animStart, key, size]);

  const uvBarWidth = `${d.uvPct}%`;

  return (
    <div className="w-full h-full grid place-items-center bg-transparent p-0">
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full h-full max-w-[560px] rounded-[52px] bg-[#111010] p-[14px] shadow-[0_0_0_1px_rgba(255,255,255,0.06),_0_60px_120px_rgba(0,0,0,0.8),_0_30px_60px_rgba(249,115,22,0.10)]"
      >
        <div className="relative overflow-hidden rounded-[40px] bg-[#1a1812] h-full flex flex-col">
          <div className="absolute left-1/2 top-3 z-20 h-7 w-40 -translate-x-1/2 rounded-full bg-black/90 shadow-[0_8px_18px_rgba(0,0,0,0.35)]" />
          {/* Status */}
          <div className="flex items-center justify-between px-6 pt-4 pb-2 text-[12px] font-semibold text-white/90">
            <span className="pl-2 text-[18px] font-bold tracking-[-0.04em] text-white">9:41</span>
            <div className="flex items-center gap-2 opacity-90">
              <div className="h-2 w-4 rounded-sm bg-white/40" />
              <div className="h-2 w-4 rounded-sm bg-white/65" />
              <div className="h-2 w-4 rounded-sm bg-white/90" />
              <div className="h-2 w-6 rounded-sm border border-white/35 bg-white/10" />
            </div>
          </div>

          <div className="pointer-events-none flex items-center justify-between px-6 pb-2 pt-1">
            <div className="text-[24px] font-bold tracking-[-0.05em] text-white drop-shadow-[0_10px_30px_rgba(0,0,0,0.4)]">
              Sunscreened
            </div>
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-white/[0.06] text-orange-300 ring-1 ring-white/10">
                <HelpCircle className="h-4 w-4" strokeWidth={2.2} />
              </div>
              <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-orange-500 to-amber-400 text-white shadow-[0_12px_30px_-18px_rgba(249,115,22,0.8)]">
                <CloudSun className="h-4 w-4" strokeWidth={2.2} />
              </div>
            </div>
          </div>

          {/* Scroll */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-32 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {/* Top cards */}
            <div className="grid grid-cols-2 gap-2.5 px-3.5 pt-2 pb-2.5">
              <div className="rounded-2xl border border-white/5 bg-[#252220] p-4">
                <div className="mb-2 flex items-center gap-1 text-[14px] font-semibold text-white/90">
                  Tampa <span className="text-white/40">›</span>
                </div>
                <div className="flex items-end">
                  <div className="text-[42px] font-bold leading-none tracking-[-0.05em] text-white">
                    57<span className="text-[24px]">°</span>
                  </div>
                  <div className="ml-2 flex flex-col gap-0.5 pb-1 text-[12px]">
                    <div className="text-white/75">↑ 63°</div>
                    <div className="text-white/50">↓ 55°</div>
                  </div>
                </div>
                <div className="mt-2 flex gap-3 text-[11px] text-white/45">
                  <div className="flex items-center gap-1">
                    <span className="opacity-70">≋</span> 9 mph SW
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="opacity-70">☁︎</span> 100%
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/5 bg-[#252220] p-4">
                <div className="mb-1.5 text-[13px] font-semibold text-white/90">UV Index</div>
                <div className="mb-2 flex items-end">
                  <div className="text-[48px] font-bold leading-none tracking-[-0.06em] text-white">
                    {d.uvIndex.toFixed(1)}
                  </div>
                  <div className="ml-2 flex flex-col gap-0.5 pb-1 text-[12px]">
                    <div className="text-white/75">↑ 5.7</div>
                    <div className="text-white/50">↓ 1.5</div>
                  </div>
                </div>
                <div className="mb-1.5 h-[5px] overflow-hidden rounded bg-white/10">
                  <motion.div
                    className="h-full rounded bg-gradient-to-r from-orange-500 to-amber-400"
                    initial={{ width: "0%" }}
                    animate={{ width: uvBarWidth }}
                    transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                  />
                </div>
                <div className="text-[12px] text-white/45">{d.uvStatus}</div>
              </div>
            </div>

            {/* Chart */}
            <div className="mx-3.5 mt-1 rounded-2xl border border-white/5 bg-[#252220] p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[16px] font-bold tracking-[-0.02em] text-white">UV &amp; Temperature</div>
                <div className="flex items-center gap-3 text-[11px] font-medium text-white/55">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-orange-500" /> UV
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-sky-500" /> Temp
                  </div>
                </div>
              </div>

              <div ref={wrapRef} className="relative h-[180px]">
                <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

                {/* left y labels */}
                <div className="pointer-events-none absolute left-0 top-0 bottom-7 flex w-7 flex-col justify-between text-[10px] text-white/25">
                  <div>70°</div>
                  <div>65°</div>
                  <div>60°</div>
                  <div>55°</div>
                  <div>50°</div>
                </div>

                {/* right y labels */}
                <div className="pointer-events-none absolute right-0 top-0 bottom-7 flex w-4 flex-col justify-between text-right text-[10px] text-white/25">
                  <div>6</div>
                  <div>4</div>
                  <div>2</div>
                  <div>0</div>
                  <div>0</div>
                </div>

                {/* x labels */}
                <div className="pointer-events-none absolute bottom-0 left-7 right-2 flex justify-between text-[10px] text-white/30">
                  {hours.map((h) => (
                    <div key={h}>{h}</div>
                  ))}
                </div>
              </div>
            </div>

            {/* Go outside */}
            <div className="mx-3.5 mt-2 rounded-2xl border border-white/5 bg-[#252220] p-5">
              <div className="mb-4 text-[22px] font-bold tracking-[-0.03em] text-white">Should I Go Outside?</div>
              <div className="flex items-center gap-4">
                <div className="text-[44px] leading-none">{d.goIcon}</div>
                <div className="text-[16px] font-normal leading-snug text-white/75">
                  <span className="text-white font-semibold">{d.goText.split(".")[0]}.</span>
                </div>
              </div>
            </div>

            {/* Status badge */}
            <div className="mx-3.5 mt-2 flex items-center gap-2.5 rounded-2xl border border-orange-500/20 bg-orange-500/10 px-4 py-3">
              <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
              <div className="text-[13px] font-medium text-orange-300">{d.sbText}</div>
            </div>

            {/* Action cards */}
            <div className="mx-3.5 mt-3 space-y-3 pb-2">
              {[
                {
                  icon: Calculator,
                  title: "Sun Exposure Calculator",
                  body:
                    "Calculate your expected tanning, optimal vitamin D time, time before burn, and recommended SPF based on current conditions.",
                  cta: "Open Calculator",
                },
                {
                  icon: Camera,
                  title: "Skin Health Analysis",
                  body:
                    "Get detailed skin analysis in seconds. Just take a photo and get metrics on evenness, texture, hydration, UV damage, and more.",
                  cta: "Analyze My Skin",
                },
                {
                  icon: TrendingUp,
                  title: "Metrics Over Time",
                  body:
                    "View detailed charts of your vitamin D exposure, UV dose, SPF usage, and session duration over time.",
                  cta: "View Metrics",
                  chevron: true,
                },
              ].map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.title} className="rounded-3xl border border-white/10 bg-black/35 p-5 shadow-[0_30px_80px_-60px_rgba(0,0,0,0.9)]">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 grid h-10 w-10 place-items-center rounded-2xl bg-orange-500/10 text-orange-300 ring-1 ring-orange-500/20">
                        <Icon className="h-5 w-5" strokeWidth={2.2} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[18px] font-semibold tracking-[-0.02em] text-white/90">{card.title}</div>
                        <div className="mt-2 text-[12.5px] leading-relaxed text-white/55">{card.body}</div>
                      </div>
                    </div>

                    <button className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-400 px-4 py-3 text-[14px] font-semibold text-black shadow-[0_20px_50px_-25px_rgba(249,115,22,0.8)]">
                      <Icon className="h-4 w-4" strokeWidth={2.4} />
                      {card.cta}
                      {card.chevron ? <span className="ml-2 text-black/70">›</span> : null}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom nav */}
          <div className="absolute inset-x-0 bottom-0 z-30 px-5 pb-4">
            <div className="relative mx-auto flex items-center justify-between rounded-[28px] border border-white/10 bg-black/55 px-5 py-3 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl">
              <button className="grid h-11 w-11 place-items-center rounded-full bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/20">
                <Home className="h-5 w-5" strokeWidth={2.2} />
              </button>
              <button className="grid h-11 w-11 place-items-center rounded-full text-white/70 hover:text-white">
                <History className="h-5 w-5" strokeWidth={2.2} />
              </button>
              <div className="mx-2 flex-1">
                <div className="mx-auto flex max-w-[170px] items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-400 px-4 py-3 text-[14px] font-semibold text-black shadow-[0_20px_50px_-30px_rgba(249,115,22,0.85)]">
                  <Sun className="h-4 w-4" strokeWidth={2.4} />
                  Session
                </div>
              </div>
              <button className="grid h-11 w-11 place-items-center rounded-full text-white/70 hover:text-white">
                <Settings className="h-5 w-5" strokeWidth={2.2} />
              </button>
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

